import React, { useState, useEffect, useRef } from 'react';
import { DB } from '../data/database';
import { ROLES, CUT_TYPES, REQUEST_STATUSES, VEHICLE_MAKES } from '../data/constants';
import { useLanguage } from '../translations/LanguageContext';
import { RequestIcon, PlusIcon, CheckIcon, DeleteIcon } from './Icons';
import { logActivity, Notifications } from '../data/notifications';
import PartsSelector from './PartsSelector';
import TimelineProgress from './ui/TimelineProgress';

const TYPE_OPTIONS = ['Vehicle', 'Engine', 'Spare Part'];

const STATUS_CLASS = {
  pending:    'pending',
  processing: 'transit',
  sourced:    'available',
  cancelled:  'assigned',
};

const EMPTY_FORM = {
  type: 'Vehicle', make: '', model: '', year: '',
  cutType: '', category: '', budget: '', details: '',
};

// ── Convert legacy staffNote to thread array ──────────────────
function getThread(request) {
  if (request.thread && request.thread.length > 0) return request.thread;
  if (request.staffNote) {
    return [{
      id: 'legacy-1',
      author: 'Staff',
      role: 'staff',
      message: request.staffNote,
      timestamp: (request.createdAt || new Date().toISOString().slice(0, 10)) + 'T00:00:00Z',
    }];
  }
  return [];
}

// ── New Request Modal ─────────────────────────────────────────
function NewRequestModal({ user, onSave, onClose }) {
  const { t } = useLanguage();
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedParts, setSelectedParts] = useState([]);
  const [showPartsPicker, setShowPartsPicker] = useState(false);
  const [error, setError] = useState('');

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isPart && selectedParts.length === 0) return setError('Please select at least one part.');
    if (!form.details.trim()) return setError('Please describe what you are looking for.');
    if (!form.budget.trim())  return setError('Please enter your budget.');

    const requests = DB.get('requests') || [];
    DB.set('requests', [...requests, {
      id:           DB.genId('REQ'),
      customerId:   user.id,
      customerName: user.name,
      type:         form.type,
      make:         form.make,
      model:        form.model,
      year:         form.year,
      cutType:      form.cutType,
      category:     form.category,
      selectedParts: isPart ? selectedParts.map(p => p.part_name) : [],
      selectedPartsDetail: isPart ? selectedParts : [],
      budget:       form.budget.trim(),
      details:      form.details.trim(),
      status:       'pending',
      createdAt:    new Date().toISOString().split('T')[0],
      staffNote:    '',
      thread:       [],
    }]);
    logActivity.requestNew(user.name, form.type);
    onSave();
  };

  const isVehicle = form.type === 'Vehicle';
  const isPart    = form.type === 'Spare Part';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">{t('req_new')}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">What are you looking for? <span className="req">*</span></label>
              <div style={{ display: 'flex', gap: 8 }}>
                {TYPE_OPTIONS.map(opt => (
                  <button type="button" key={opt}
                    className={`req-type-btn${form.type === opt ? ' active' : ''}`}
                    onClick={() => { setForm({ ...EMPTY_FORM, type: opt }); setSelectedParts([]); }}
                  >{opt}</button>
                ))}
              </div>
            </div>

            {isVehicle && (
              <div className="form-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <div className="form-group">
                  <label className="form-label">Make</label>
                  <select className="form-select" value={form.make} onChange={e => set('make', e.target.value)}>
                    <option value="">Any make</option>
                    {VEHICLE_MAKES.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Model</label>
                  <input className="form-input" placeholder="e.g. Supra, Skyline" value={form.model} onChange={e => set('model', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Year</label>
                  <input className="form-input" placeholder="e.g. 1998–2002" value={form.year} onChange={e => set('year', e.target.value)} />
                </div>
              </div>
            )}

            {isVehicle && (
              <div className="form-group">
                <label className="form-label">Cut Type Preference</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" className={`req-type-btn${form.cutType === '' ? ' active' : ''}`} onClick={() => set('cutType', '')}>Any</button>
                  {CUT_TYPES.map(ct => (
                    <button type="button" key={ct.id}
                      className={`req-type-btn${form.cutType === ct.id ? ' active' : ''}`}
                      onClick={() => set('cutType', ct.id)}
                    >{ct.name}</button>
                  ))}
                </div>
              </div>
            )}

            {isPart && (
              <div className="form-group">
                <label className="form-label">Parts <span className="req">*</span></label>
                <button type="button" className="btn btn-secondary" style={{ width: '100%', marginBottom: 8 }} onClick={() => setShowPartsPicker(true)}>
                  {selectedParts.length > 0 ? `Edit Parts (${selectedParts.length} selected)` : 'Select Parts from Catalog'}
                </button>
                {selectedParts.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {selectedParts.map(p => (
                      <span key={p.part_name} className="req-tag" style={{ fontSize: 10 }}>
                        {p.part_name}{p.quantity > 1 ? ` ×${p.quantity}` : ''}
                        <button type="button" onClick={() => setSelectedParts(prev => prev.filter(x => x.part_name !== p.part_name))}
                          style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', marginLeft: 4, fontSize: 12, padding: 0 }}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Budget <span className="req">*</span></label>
              <input className="form-input" placeholder="e.g. $5,000 or ¥700,000" value={form.budget} onChange={e => set('budget', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Details <span className="req">*</span></label>
              <textarea className="form-input" rows={4}
                placeholder="Describe what you're looking for — condition, colour, mileage, specific features, etc."
                value={form.details} onChange={e => set('details', e.target.value)}
                style={{ resize: 'vertical' }}
              />
            </div>
            {error && <div style={{ color: 'var(--red)', fontSize: 13 }}>{error}</div>}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary"><CheckIcon />Submit Request</button>
          </div>
        </form>

        {/* Parts Selector overlay */}
        {showPartsPicker && (
          <PartsSelector
            mode="request"
            initialSelection={selectedParts}
            onCancel={() => setShowPartsPicker(false)}
            onConfirm={(parts) => { setSelectedParts(parts); setShowPartsPicker(false); }}
          />
        )}
      </div>
    </div>
  );
}

// ── Parts Processing Panel (staff only — read-only prices) ──
const PART_STATUSES = [
  { id: 'pending',     label: 'Pending',     color: 'var(--yellow)' },
  { id: 'sourced',     label: 'Sourced',     color: 'var(--green)'  },
  { id: 'unavailable', label: 'Unavailable', color: 'var(--red)'    },
];

const PartsProcessingPanel = React.memo(function PartsProcessingPanel({ partsData, onChange }) {
  const [expanded, setExpanded] = useState(true);

  const totalParts = partsData.length;
  const sourcedCount = partsData.filter(p => p.status === 'sourced').length;

  const updatePart = (idx, field, value) => {
    const updated = partsData.map((p, i) => i === idx ? { ...p, [field]: value } : p);
    onChange(updated);
  };

  const setAllStatus = (status) => {
    onChange(partsData.map(p => ({ ...p, status })));
  };

  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <div
        onClick={() => setExpanded(e => !e)}
        style={{
          padding: '10px 20px', cursor: 'pointer', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between',
          background: 'var(--bg2)', userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 10, color: 'var(--text3)' }}>{expanded ? '▼' : '▶'}</span>
          <span style={{ fontSize: 12, fontWeight: 700 }}>Parts Processing</span>
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>
            {sourcedCount}/{totalParts} sourced
          </span>
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '0 12px 12px' }}>
          {/* Bulk status actions (no price editing) */}
          <div style={{ display: 'flex', gap: 6, padding: '8px 0', borderBottom: '1px solid var(--border)', marginBottom: 6 }}>
            <span style={{ fontSize: 10, color: 'var(--text3)', alignSelf: 'center' }}>Set all:</span>
            {PART_STATUSES.map(s => (
              <button key={s.id} type="button" className="btn btn-secondary btn-sm"
                style={{ fontSize: 10, padding: '2px 8px' }}
                onClick={() => setAllStatus(s.id)}>
                {s.label}
              </button>
            ))}
          </div>

          {/* Per-part rows — status editable, price READ-ONLY */}
          {partsData.map((part, idx) => (
            <div key={part.name} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '5px 4px',
              borderBottom: idx < totalParts - 1 ? '1px solid var(--border)' : 'none',
              opacity: part.status === 'unavailable' ? 0.5 : 1,
            }}>
              <div style={{ flex: 1, fontSize: 12, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {part.name}
                {part.created && (
                  <span style={{ fontSize: 9, color: 'var(--green)', marginLeft: 6, fontWeight: 600 }}>✓ In Inventory</span>
                )}
              </div>
              <select
                value={part.status}
                onChange={e => updatePart(idx, 'status', e.target.value)}
                style={{
                  fontSize: 10, padding: '3px 4px', borderRadius: 4, border: '1px solid var(--border)',
                  background: 'var(--bg3)', color: PART_STATUSES.find(s => s.id === part.status)?.color || 'var(--text)',
                  fontWeight: 600, width: 90, flexShrink: 0,
                }}
              >
                {PART_STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              {/* Price — read-only display */}
              <span style={{ fontSize: 11, fontWeight: 600, flexShrink: 0, minWidth: 60, textAlign: 'right', color: Number(part.price) > 0 ? 'var(--green)' : 'var(--text3)' }}>
                {Number(part.price) > 0 ? `$${Number(part.price).toLocaleString()} ✅` : '—'}
              </span>
            </div>
          ))}

          {/* Totals */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', padding: '8px 4px 0',
            borderTop: '1px solid var(--border)', marginTop: 4, fontSize: 12,
          }}>
            <span style={{ color: 'var(--text3)' }}>
              {sourcedCount} sourced · {partsData.filter(p => p.status === 'unavailable').length} unavailable · {partsData.filter(p => p.status === 'pending').length} pending
            </span>
          </div>
        </div>
      )}
    </div>
  );
});

// ── Request Thread Modal ──────────────────────────────────────
function RequestThreadModal({ request, user, role, initialView, onSave, onClose }) {
  const { t } = useLanguage();
  const [thread,  setThread]  = useState(() => getThread(request));
  const [req,     setReq]     = useState(request);
  const [message, setMessage] = useState('');
  const [status,  setStatus]  = useState(request.status);
  const [activeView, setActiveView] = useState(initialView || null); // 'chat' | 'quote' | 'cutplan' | null
  const bottomRef = useRef(null);

  const isStaff  = !role.isCustomer;
  const isClosed = req.status === 'cancelled';

  // Parts processing state (staff only)
  const [partsData, setPartsData] = useState(() => {
    // Load from saved partsProcessing or init from selectedParts
    if (request.partsProcessing?.length > 0) return request.partsProcessing;
    if (request.selectedParts?.length > 0) {
      return request.selectedParts.map(name => ({ name, status: 'pending', price: '' }));
    }
    return [];
  });

  // Scroll to bottom whenever thread changes
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread]);

  const persist = (updatedReq) => {
    // Always persist current partsProcessing
    const withParts = partsData.length > 0
      ? { ...updatedReq, partsProcessing: partsData }
      : updatedReq;
    const requests = DB.get('requests') || [];
    DB.set('requests', requests.map(r => r.id === withParts.id ? withParts : r));
    setReq(withParts);
    onSave?.();
  };

  const persistPartsOnly = (newPartsData) => {
    setPartsData(newPartsData);
    const updated = { ...req, partsProcessing: newPartsData };
    const requests = DB.get('requests') || [];
    DB.set('requests', requests.map(r => r.id === updated.id ? updated : r));
    setReq(updated);
    onSave?.();
  };

  // Check if all parts have FOB prices (from accountant via Price Quotes)
  const allPartsHaveFob = partsData.length > 0 && partsData.every(p => p.status === 'unavailable' || Number(p.price) > 0);
  const pricingRequested = req.pricingRequestedAt || false;

  const sendQuote = () => {
    if (!allPartsHaveFob) return;
    const sourced = partsData.filter(p => p.status !== 'unavailable' && Number(p.price) > 0);
    const unavail = partsData.filter(p => p.status === 'unavailable');
    if (sourced.length === 0) return;
    const total = sourced.reduce((s, p) => s + (Number(p.price) || 0), 0);
    const lines = sourced.map(p => `  • ${p.name} — $${Number(p.price).toLocaleString()}`).join('\n');
    const unavailLines = unavail.length > 0
      ? '\n\nUnavailable:\n' + unavail.map(p => `  ✕ ${p.name}`).join('\n')
      : '';
    const quoteMsg = `Parts Quote:\n${lines}\n\nTotal: $${total.toLocaleString()}${unavailLines}`;
    setMessage(quoteMsg);
  };

  // Send parts to accountant's Price Quotes queue
  const requestPricing = () => {
    const allQuotes = DB.get('priceQuotes') || [];
    const unitEntries = partsData
      .filter(p => p.status !== 'unavailable')
      .map(p => ({
        unit_id: `req_part_${req.id}_${p.name}`,
        unit_type: 'spare_part',
        unit_sku: req.vehicleSku || req.id,
        unit_description: `${p.name} — from ${req.vehicleName || req.make || ''} ${req.model || ''}`.trim(),
        fob_price: null,
        pricing: null,
        status: 'pending',
        accountant_notes: '',
        admin_approved: false,
        _source_request_id: req.id,
        _part_name: p.name,
      }));

    if (unitEntries.length === 0) return;

    const quote = {
      id: DB.genId('PQ'),
      customerId: req.customerId,
      customerName: req.customerName,
      requestedBy: req.customerId,
      requestedByName: req.customerName,
      status: 'pending',
      units: unitEntries,
      customer_notes: `Parts pricing for request #${req.id} — ${req.vehicleName || ''}`,
      sourceRequestId: req.id,
      assigned_to: null,
      createdAt: new Date().toISOString().split('T')[0],
      submittedAt: new Date().toISOString(),
    };
    DB.set('priceQuotes', [...allQuotes, quote]);

    // Mark request as pricing-requested
    const updatedReq = { ...req, pricingRequestedAt: new Date().toISOString(), pricingQuoteId: quote.id };
    const requests = DB.get('requests') || [];
    DB.set('requests', requests.map(r => r.id === updatedReq.id ? updatedReq : r));
    setReq(updatedReq);

    Notifications.add('price_updated', 'Parts Pricing Needed',
      `Parts pricing needed for ${req.customerName} — ${unitEntries.length} parts from ${req.vehicleName || 'request #' + req.id}`, {});

    onSave?.();
  };

  // Convert sourced parts → spare parts inventory
  const createInventoryItems = () => {
    const toCreate = partsData.filter(p => p.status === 'sourced' && !p.created);
    if (toCreate.length === 0) return;

    const parts = DB.get('spareParts') || [];
    const vehicleName = req.vehicleName || (req.make ? `${req.make} ${req.model}` : '');
    const chassis = req.chassis || '';
    const newParts = [];

    toCreate.forEach(p => {
      const id  = DB.genId('PRT');
      const sku = `PRT-${(chassis || req.id).substring(0, 6).toUpperCase()}-${String(parts.length + newParts.length + 1).padStart(3, '0')}`;
      newParts.push({
        id, sku,
        partName:   vehicleName ? `${vehicleName} — ${p.name}` : p.name,
        category:   p.name,
        condition:  'Used',
        fitment:    chassis,
        quantity:   1,
        status:     'assigned',
        assignedTo: req.customerId,
        priceJPY:   Number(p.price) || 0,
        costJPY:    0,
        images:     [],
        daysInStock: 0,
        createdAt:  new Date().toISOString().split('T')[0],
        sourceRequest: req.id,
        sourceVehicle: req.vehicleId || null,
      });
    });

    DB.set('spareParts', [...parts, ...newParts]);

    // Mark parts as created
    const updated = partsData.map(p =>
      p.status === 'sourced' && !p.created
        ? { ...p, created: true, inventorySku: newParts.find(n => n.partName.includes(p.name))?.sku }
        : p
    );
    setPartsData(updated);

    // Update request
    const updatedReq = { ...req, partsProcessing: updated, status: 'sourced' };
    const requests = DB.get('requests') || [];
    DB.set('requests', requests.map(r => r.id === updatedReq.id ? updatedReq : r));
    setReq(updatedReq);
    setStatus('sourced');
    onSave?.();

    // Auto-message
    const msg = `${newParts.length} part${newParts.length !== 1 ? 's' : ''} added to your inventory and assigned to your account.`;
    const newMsg = {
      id: `msg-${Date.now()}`,
      author: user.name,
      role: 'staff',
      message: msg,
      timestamp: new Date().toISOString(),
    };
    const newThread = [...thread, newMsg];
    setThread(newThread);
    persist({ ...updatedReq, thread: newThread });
  };

  // Create a cut plan from this request
  const createCutPlan = () => {
    if (!req.vehicleId) return;
    const cutPlans = DB.get('cutPlans') || [];
    const vehicleName = req.vehicleName || `${req.make || ''} ${req.model || ''}`.trim();
    const planId = DB.genId('CP');
    const plan = {
      id: planId,
      vehicleId: req.vehicleId,
      vehicleInfo: {
        name: vehicleName,
        sku: req.vehicleSku || '',
        chassis: req.chassis || '',
        year: req.year || '',
      },
      sourceRequestId: req.id,
      customerId: req.customerId,
      customerName: req.customerName,
      parts: (req.selectedParts || []).map(name => {
        const pd = partsData.find(p => p.name === name);
        return {
          part_name: name,
          status: pd?.status || 'pending',
          price: pd?.price || '',
          assigned_to: req.customerId,
        };
      }),
      status: 'draft',
      createdBy: user.name,
      createdAt: new Date().toISOString(),
    };
    DB.set('cutPlans', [...cutPlans, plan]);

    // Link request to cut plan
    const updatedReq = { ...req, linked_cut_plan_id: planId, partsProcessing: partsData };
    const requests = DB.get('requests') || [];
    DB.set('requests', requests.map(r => r.id === updatedReq.id ? updatedReq : r));
    setReq(updatedReq);
    onSave?.();

    // Auto-message
    const msg = `Cut plan created for ${vehicleName}. ${plan.parts.length} parts queued for processing.`;
    const newMsg = { id: `msg-${Date.now()}`, author: user.name, role: 'staff', message: msg, timestamp: new Date().toISOString() };
    const newThread = [...thread, newMsg];
    setThread(newThread);
    persist({ ...updatedReq, thread: newThread });
  };

  const sendMessage = () => {
    const text = message.trim();
    if (!text) return;
    const newMsg = {
      id:        `msg-${Date.now()}`,
      author:    user.name,
      role:      role.isCustomer ? 'customer' : 'staff',
      message:   text,
      timestamp: new Date().toISOString(),
    };
    const newThread = [...thread, newMsg];
    setThread(newThread);
    setMessage('');
    persist({ ...req, thread: newThread, staffNote: '' });
    // Notify the other party
    logActivity.requestReplied(req.id, user.name, !role.isCustomer, role.isCustomer ? null : req.customerId);
  };

  const changeStatus = (newStatus) => {
    setStatus(newStatus);
    logActivity.requestStatusChanged?.(req.id, newStatus, req.customerName, req.customerId);
    const timelineEntry = {
      stage: newStatus,
      date: new Date().toISOString(),
      note: `Status changed to ${newStatus.replace(/_/g, ' ')}`,
      by: role.isCustomer ? 'customer' : 'staff',
      by_name: user.name,
    };
    const existingTimeline = Array.isArray(req.timeline) ? req.timeline : [];
    persist({ ...req, status: newStatus, timeline: [...existingTimeline, timelineEntry] });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) sendMessage();
  };

  const fmtTime = (iso) => {
    try {
      return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  const cutName = CUT_TYPES.find(c => c.id === req.cutType)?.name;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h3 className="modal-title" style={{ fontSize: 16 }}>
                {isStaff ? request.customerName : 'Your Request'}
              </h3>
              <span className={`status status-${STATUS_CLASS[req.status] || req.status}`} style={{ fontSize: 10 }}>
                {REQUEST_STATUSES.find(s => s.id === req.status)?.name || req.status}
              </span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>
              {req.type}
              {req.make  && ` · ${req.make} ${req.model}`}
              {req.year  && ` · ${req.year}`}
              {cutName   && ` · ${cutName}`}
              {req.budget && ` · Budget: ${req.budget}`}
              {req.selectedParts?.length > 0 && ` · ${req.selectedParts.length} parts`}
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body" style={{ padding: 0, display: 'flex', flexDirection: 'column', maxHeight: '75vh' }}>

          {/* ── View tabs (when opened from a specific button) ── */}
          {activeView && (
            <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)' }}>
              {[
                { id: 'chat', label: '💬 Reply', show: true },
                { id: 'quote', label: '💰 Send to Accountant', show: true },
                { id: 'cutplan', label: '✂️ Cut Plan', show: !!req.vehicleId },
              ].filter(v => v.show).map(v => (
                <button key={v.id} onClick={() => setActiveView(v.id)} style={{
                  padding: '10px 16px', fontSize: 12, fontWeight: activeView === v.id ? 600 : 400,
                  background: activeView === v.id ? 'var(--bg3)' : 'transparent',
                  color: activeView === v.id ? 'var(--text1)' : 'var(--text3)',
                  border: 'none', borderBottom: activeView === v.id ? '2px solid var(--blue)' : '2px solid transparent',
                  cursor: 'pointer',
                }}>{v.label}</button>
              ))}
              <button onClick={() => setActiveView(null)} style={{ marginLeft: 'auto', padding: '10px 12px', fontSize: 10, color: 'var(--text3)', background: 'none', border: 'none', cursor: 'pointer' }}>Show all ↗</button>
            </div>
          )}

          {/* ── CHAT VIEW (Reply only) ── */}
          {activeView === 'chat' && (<>
            <div style={{ padding: '10px 20px', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--text3)' }}>
              RE: {req.type}{req.make ? ` · ${req.make} ${req.model}` : ''}{cutName ? ` · ${cutName}` : ''}
            </div>
            <div className="req-thread-scroll" style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {thread.length === 0 && <div style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 13, padding: '20px 0' }}>No messages yet.</div>}
              {thread.map(msg => {
                const isOwn = role.isCustomer ? msg.role === 'customer' : msg.role === 'staff';
                return (
                  <div key={msg.id} className={`req-thread-row${isOwn ? ' own' : ''}`}>
                    <div className={`req-thread-bubble${msg.role === 'staff' ? ' staff' : ' customer'}`}>{msg.message}</div>
                    <div className="req-thread-meta">{msg.author} · {fmtTime(msg.timestamp)}</div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
            {!isClosed && (
              <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                <textarea className="form-input" rows={2} placeholder="Type your reply..." value={message} onChange={e => setMessage(e.target.value)} onKeyDown={handleKeyDown} style={{ flex: 1, resize: 'none', fontSize: 13, minHeight: 0 }} />
                <button className="btn btn-primary" disabled={!message.trim()} onClick={sendMessage} style={{ flexShrink: 0 }}>Send</button>
              </div>
            )}
          </>)}

          {/* ── QUOTE VIEW ── */}
          {activeView === 'quote' && (() => {
            const [quoteNotes, setQuoteNotes] = React.useState('');
            const [sent, setSent] = React.useState(false);

            const handleSendToAccountant = () => {
              const pqList = DB.get('priceQuotes') || [];
              const parts = req.selectedParts || [];
              const entries = (parts.length > 0 ? parts : [req.cutType || req.type || 'Part']).map(p => ({
                unit_id: `req_part_${req.id}_${p}`,
                unit_type: 'spare_part',
                unit_sku: req.vehicleSku || req.id,
                unit_description: `${p} — from ${req.vehicleName || req.make || ''} ${req.model || ''}`.trim(),
                fob_price: null, pricing: null, status: 'pending',
                accountant_notes: '', admin_approved: false,
                _source_request_id: req.id, _part_name: p,
              }));
              const q = {
                id: DB.genId('PQ'), customerId: req.customerId, customerName: req.customerName,
                requestedBy: req.customerId, requestedByName: req.customerName,
                status: 'pending', units: entries,
                customer_notes: quoteNotes || `Pricing for request #${req.id}`,
                sourceRequestId: req.id,
                createdAt: new Date().toISOString().split('T')[0], submittedAt: new Date().toISOString(),
              };
              DB.set('priceQuotes', [...pqList, q]);
              const updated = { ...req, pricingRequestedAt: new Date().toISOString(), pricingQuoteId: q.id };
              persist(updated);
              Notifications.add('price_updated', 'Pricing Needed',
                `Pricing needed for ${req.customerName} — ${entries.length} item${entries.length !== 1 ? 's' : ''} from request #${req.id}`, {});
              setSent(true);
            };

            return (
              <div style={{ padding: 20, flex: 1, overflowY: 'auto' }}>
                {sent ? (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text1)', marginBottom: 8 }}>Sent to Accountant</div>
                    <div style={{ fontSize: 13, color: 'var(--text3)' }}>The accountant will set prices in Price Quotes. You'll be notified when done.</div>
                    <button className="btn btn-secondary btn-sm" style={{ marginTop: 16 }} onClick={() => setActiveView(null)}>Back to request</button>
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text1)', marginBottom: 4 }}>Send to Accountant for Pricing</div>
                    <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
                      {req.vehicleName || `${req.make || ''} ${req.model || ''}`.trim()}{cutName ? ` · ${cutName}` : ''}
                    </div>
                    <div style={{ padding: 12, background: 'var(--bg3)', borderRadius: 8, marginBottom: 16, fontSize: 12, color: 'var(--text2)' }}>
                      The accountant will receive this in their Price Quotes queue and set the FOB price for each item. Once priced and approved by admin, the customer will see the quote.
                    </div>
                    <div className="form-group">
                      <label className="form-label">Notes for accountant (optional)</label>
                      <textarea className="form-input" rows={3} value={quoteNotes} onChange={e => setQuoteNotes(e.target.value)} placeholder="e.g. Customer wants competitive pricing, urgent..." style={{ resize: 'vertical' }} />
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <button className="btn btn-secondary" onClick={() => setActiveView(null)}>Cancel</button>
                      <button className="btn btn-primary" onClick={handleSendToAccountant}>💰 Send to Accountant</button>
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          {/* ── CUT PLAN VIEW ── */}
          {activeView === 'cutplan' && (() => {
            const [cutType, setCutType] = React.useState(req.cutType || '');
            const [customCutType, setCustomCutType] = React.useState('');
            const [showCustomInput, setShowCustomInput] = React.useState(false);
            const [cutDetails, setCutDetails] = React.useState({});
            const [cutNotes, setCutNotes] = React.useState('');
            const isAdmin = user.role === 'admin' || user.role === 'head_admin';

            // All available cut/part types
            const ALL_TYPES = [
              ...CUT_TYPES.map(ct => ct.name),
              'CBU', 'Engine Only', 'Door', 'Bumper', 'Axle', 'Transmission',
              'Lights', 'Suspension', 'Dashboard', 'Seats', 'Mirrors',
              'Radiator', 'AC Compressor', 'Steering Rack', 'Differential',
            ];

            const handleSubmitCutPlan = (approve) => {
              const finalType = cutType || customCutType;
              if (!finalType) { alert('Please select a cut type.'); return; }
              const cutPlans = DB.get('cutPlans') || [];
              const plan = {
                id: DB.genId('CP'), vehicleId: req.vehicleId, vehicleSku: req.vehicleSku,
                vehicleName: req.vehicleName || `${req.make || ''} ${req.model || ''}`.trim(),
                requestId: req.id, customerId: req.customerId, customerName: req.customerName,
                cutType: finalType, cutDetails, notes: cutNotes,
                status: approve ? 'processing' : 'pending_approval',
                createdBy: user.id, createdByName: user.name,
                createdAt: new Date().toISOString(),
              };
              DB.set('cutPlans', [...cutPlans, plan]);
              const updated = { ...req, linked_cut_plan_id: plan.id, status: approve ? 'processing' : 'pending' };
              persist(updated);
              if (approve && req.vehicleId) {
                const updateUnitStatus = require('../utils/updateUnitStatus').default;
                updateUnitStatus(req.vehicleId, 'vehicle', 'cutting', { updatedBy: user.name });
              }
              alert(approve ? '✅ Cut plan approved and processing started.' : '📤 Cut plan submitted for admin approval.');
              onClose();
            };

            return (
              <div style={{ padding: 20, flex: 1, overflowY: 'auto' }}>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 4 }}>Source unit:</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text1)', marginBottom: 16 }}>
                  {req.vehicleName || `${req.make || ''} ${req.model || ''}`.trim() || 'Unit'} {req.vehicleSku ? `· ${req.vehicleSku}` : ''}
                </div>

                <div className="form-group" style={{ marginBottom: 14 }}>
                  <label className="form-label">Select cut / part type</label>
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    {ALL_TYPES.map(name => (
                      <button key={name} type="button" onClick={() => { setCutType(name); setShowCustomInput(false); setCustomCutType(''); }}
                        style={{ padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: cutType === name ? 600 : 400, border: 'none', cursor: 'pointer', background: cutType === name ? 'var(--blue)' : 'var(--bg3)', color: cutType === name ? '#fff' : 'var(--text2)' }}>
                        {name}
                      </button>
                    ))}
                    <button type="button" onClick={() => { setShowCustomInput(true); setCutType(''); }}
                      style={{ padding: '5px 12px', borderRadius: 6, fontSize: 11, border: '1px dashed var(--border)', cursor: 'pointer', background: showCustomInput ? 'var(--blue)' : 'transparent', color: showCustomInput ? '#fff' : 'var(--text3)' }}>
                      + Custom
                    </button>
                  </div>
                  {showCustomInput && (
                    <input className="form-input" style={{ marginTop: 8, fontSize: 13 }} value={customCutType} onChange={e => setCustomCutType(e.target.value)} placeholder="Type custom cut/part name..." autoFocus />
                  )}
                </div>

                {cutType && (
                  <div style={{ padding: 14, background: 'var(--bg3)', borderRadius: 10, marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 10 }}>What's included?</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                      {[
                        ['engine', 'Engine'], ['transmission', 'Transmission'], ['front_suspension', 'Front suspension'],
                        ['dashboard', 'Dashboard'], ['steering_rack', 'Steering rack'], ['ac_compressor', 'AC compressor'],
                        ['front_doors', 'Front doors'], ['rear_doors', 'Rear doors'],
                        ['front_bumper', 'Front bumper'], ['rear_bumper', 'Rear bumper'],
                        ['radiator', 'Radiator'], ['front_lights', 'Front lights'],
                        ['rear_lights', 'Rear lights'], ['back_differential', 'Back differential'],
                        ['seats', 'Seats'], ['with_catalyst', 'With catalyst'],
                      ].map(([key, label]) => (
                        <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                          <input type="checkbox" checked={!!cutDetails[key]} onChange={e => setCutDetails(prev => ({ ...prev, [key]: e.target.checked }))} style={{ accentColor: 'var(--green)' }} />
                          {label}
                        </label>
                      ))}
                    </div>
                    <div className="form-group" style={{ marginTop: 10 }}>
                      <label className="form-label" style={{ fontSize: 11 }}>Additional items</label>
                      <input className="form-input" value={cutDetails.additional || ''} onChange={e => setCutDetails(prev => ({ ...prev, additional: e.target.value }))} placeholder="e.g. Front axle assembly" />
                    </div>
                  </div>
                )}

                <div className="form-group" style={{ marginBottom: 14 }}>
                  <label className="form-label">Notes</label>
                  <textarea className="form-input" rows={2} value={cutNotes} onChange={e => setCutNotes(e.target.value)} placeholder="e.g. Customer wants clean cut, no damage to dashboard" style={{ resize: 'vertical' }} />
                </div>

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button className="btn btn-secondary" onClick={() => setActiveView(null)}>Cancel</button>
                  {isAdmin ? (
                    <button className="btn btn-success" disabled={!cutType} onClick={() => handleSubmitCutPlan(true)}>✅ Approve & Start Processing</button>
                  ) : (
                    <button className="btn btn-primary" disabled={!cutType} onClick={() => handleSubmitCutPlan(false)}>📤 Submit for Admin Approval</button>
                  )}
                </div>
              </div>
            );
          })()}

          {/* ── FULL VIEW (default — no activeView set) ── */}
          {!activeView && <>

          {/* ── Vehicle link ── */}
          {req.vehicleId && (
            <div style={{
              padding: '10px 20px', borderBottom: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
              background: 'rgba(88,166,255,0.05)',
            }}>
              <span style={{ fontSize: 16 }}>🚗</span>
              <div>
                <div style={{ fontWeight: 600 }}>{req.vehicleName || `${req.make} ${req.model}`}</div>
                <div style={{ fontSize: 10, color: 'var(--text3)' }}>
                  {req.vehicleSku}{req.chassis && ` · ${req.chassis}`}{req.year && ` · ${req.year}`}
                </div>
              </div>
            </div>
          )}

          {/* ── Timeline Progress ── */}
          {(() => {
            const isPartsOrder = req.type === 'parts_from_vehicle' || req.vehicleId;
            const stages = isPartsOrder ? [
              { id: 'submitted', label: 'Submitted', icon: '📝' },
              { id: 'reviewing', label: 'Reviewing', icon: '🔍' },
              { id: 'stock_check', label: 'Stock Check', icon: '📦' },
              { id: 'pricing', label: 'Pricing', icon: '💰' },
              { id: 'quoted', label: 'Quoted', icon: '📨' },
              { id: 'accepted', label: 'Accepted', icon: '✅' },
              { id: 'completed', label: 'Done', icon: '🏁' },
            ] : [
              { id: 'submitted', label: 'Submitted', icon: '📝' },
              { id: 'reviewing', label: 'Reviewing', icon: '🔍' },
              { id: 'sourcing', label: 'Sourcing', icon: '🔎' },
              { id: 'quoted', label: 'Quoted', icon: '💰' },
              { id: 'completed', label: 'Done', icon: '🏁' },
            ];
            const statusToCompleted = {
              pending: ['submitted'],
              processing: ['submitted', 'reviewing'],
              reviewing: ['submitted', 'reviewing'],
              stock_checked: ['submitted', 'reviewing', 'stock_check'],
              cutting: ['submitted', 'reviewing', 'stock_check'],
              pricing: ['submitted', 'reviewing', 'stock_check', 'pricing'],
              quoted: ['submitted', 'reviewing', 'stock_check', 'pricing', 'quoted'],
              accepted: ['submitted', 'reviewing', 'stock_check', 'pricing', 'quoted', 'accepted'],
              completed: ['submitted', 'reviewing', 'stock_check', 'pricing', 'quoted', 'accepted', 'completed'],
              sourced: ['submitted', 'reviewing', 'stock_check', 'pricing', 'quoted'],
              cancelled: ['submitted'],
            };
            const completed = statusToCompleted[req.status] || ['submitted'];
            const current = completed[completed.length - 1];
            const tlData = {};
            (Array.isArray(req.timeline) ? req.timeline : []).forEach(e => {
              if (e.stage) tlData[e.stage] = { date: e.date, note: e.note, by_name: e.by_name };
            });
            if (!tlData.submitted) tlData.submitted = { date: req.createdAt };
            return (
              <div style={{ padding: '8px 20px', borderBottom: '1px solid var(--border)', overflowX: 'auto' }}>
                <TimelineProgress stages={stages} currentStage={current} completedStages={completed} stageData={tlData} compact />
              </div>
            );
          })()}

          {/* ── Original request details ── */}
          <div style={{ padding: '14px 20px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)', fontSize: 13, color: 'var(--text2)' }}>
            <div style={{ fontStyle: 'italic', marginBottom: req.selectedParts?.length > 0 ? 10 : 0 }}>"{req.details}"</div>
            {/* Customer view: show parts list as tags */}
            {!isStaff && req.selectedParts?.length > 0 && (
              <div>
                <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 600, marginBottom: 4 }}>Parts requested ({req.selectedParts.length}):</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {req.selectedParts.map(p => {
                    const pd = partsData.find(d => d.name === p);
                    const statusColor = pd?.status === 'sourced' ? 'var(--green)' : pd?.status === 'unavailable' ? 'var(--red)' : 'var(--text3)';
                    return (
                      <span key={p} className="req-tag" style={{ fontSize: 10, borderColor: statusColor }}>
                        {p}
                        {pd?.status === 'sourced' && pd?.price && <span style={{ color: 'var(--green)', marginLeft: 4 }}>¥{Number(pd.price).toLocaleString()}</span>}
                        {pd?.status === 'unavailable' && <span style={{ color: 'var(--red)', marginLeft: 4 }}>✕</span>}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ── Staff: Parts processing panel ── */}
          {isStaff && partsData.length > 0 && (
            <>
              <PartsProcessingPanel partsData={partsData} onChange={persistPartsOnly} />
              <div style={{ padding: '8px 20px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {/* CHECK STOCK — always available */}
                {partsData.some(p => p.status === 'sourced' && !p.created) && (
                  <button type="button" className="btn btn-success btn-sm" style={{ fontSize: 10 }} onClick={createInventoryItems}>
                    <CheckIcon /> Check Stock ({partsData.filter(p => p.status === 'sourced' && !p.created).length})
                  </button>
                )}
                {partsData.every(p => p.status !== 'sourced' || p.created) && partsData.some(p => p.created) && (
                  <span style={{ fontSize: 10, color: 'var(--green)', fontWeight: 600, alignSelf: 'center' }}>✓ Stock checked</span>
                )}

                {/* CREATE CUT PLAN — opens cutplan view in this popup */}
                {!req.linked_cut_plan_id && (
                  <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 10 }} onClick={() => setActiveView('cutplan')}>
                    ✂️ Create Cut Plan
                  </button>
                )}
                {req.linked_cut_plan_id && (
                  <span style={{ fontSize: 10, color: 'var(--cyan)', fontWeight: 600, alignSelf: 'center' }}>✂️ Cut Plan: {req.linked_cut_plan_id}</span>
                )}

                {/* REQUEST PRICING — sends to accountant */}
                {!pricingRequested ? (
                  <button type="button" className="btn btn-primary btn-sm" style={{ fontSize: 10 }} onClick={requestPricing}>
                    💰 Request Pricing
                  </button>
                ) : (
                  <span style={{ fontSize: 10, color: 'var(--yellow)', fontWeight: 600, alignSelf: 'center' }}>Pricing Requested 🟡</span>
                )}

                {/* SEND QUOTE — only enabled when ALL parts are priced */}
                <button type="button" className="btn btn-primary btn-sm" style={{ fontSize: 10 }}
                  disabled={!allPartsHaveFob}
                  title={!allPartsHaveFob ? 'All parts must be priced before sending quote. Send to accountant for pricing.' : 'Send quote to customer'}
                  onClick={sendQuote}>
                  📨 Send Quote
                </button>
              </div>
            </>
          )}

          {/* ── Staff: status controls ── */}
          {isStaff && (
            <div style={{ padding: '10px 20px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: 'var(--text3)', marginRight: 4 }}>Status:</span>
              {REQUEST_STATUSES.map(s => (
                <button
                  key={s.id}
                  type="button"
                  className={`req-type-btn${status === s.id ? ' active' : ''}`}
                  style={{ fontSize: 11, padding: '4px 10px' }}
                  onClick={() => changeStatus(s.id)}
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}

          {/* ── Thread messages ── */}
          <div className="req-thread-scroll" style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {thread.length === 0 && (
              <div style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 13, padding: '20px 0' }}>
                No messages yet. {isClosed ? 'This request is closed.' : 'Start the conversation below.'}
              </div>
            )}
            {thread.map(msg => {
              const isOwn = role.isCustomer ? msg.role === 'customer' : msg.role === 'staff';
              return (
                <div key={msg.id} className={`req-thread-row${isOwn ? ' own' : ''}`}>
                  <div className={`req-thread-bubble${msg.role === 'staff' ? ' staff' : ' customer'}`}>
                    {msg.message}
                  </div>
                  <div className="req-thread-meta">
                    {msg.author} · {fmtTime(msg.timestamp)}
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          {/* ── Closed banner ── */}
          {isClosed && (
            <div style={{ padding: '10px 20px', background: 'rgba(248,81,73,0.08)', borderTop: '1px solid rgba(248,81,73,0.2)', fontSize: 13, color: 'var(--red)', textAlign: 'center' }}>
              This request has been closed.
              {isStaff && (
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ marginLeft: 12, fontSize: 11 }}
                  onClick={() => changeStatus('pending')}
                >
                  Reopen
                </button>
              )}
            </div>
          )}

          {/* ── Message input ── */}
          {!isClosed && (
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: 10, alignItems: 'flex-end' }}>
              <textarea
                className="form-input"
                rows={2}
                placeholder={isStaff ? 'Reply to customer… (Ctrl+Enter to send)' : 'Add a reply… (Ctrl+Enter to send)'}
                value={message}
                onChange={e => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{ flex: 1, resize: 'none', fontSize: 13, minHeight: 0 }}
              />
              <button
                className="btn btn-primary"
                style={{ flexShrink: 0, alignSelf: 'stretch' }}
                disabled={!message.trim()}
                onClick={sendMessage}
              >
                Send
              </button>
            </div>
          )}

          </>}
        </div>
      </div>
    </div>
  );
}

// ── Main Requests Page ────────────────────────────────────────
function Requests({ user, lang, setPage }) {
  const { t } = useLanguage();
  const role = ROLES[user.role] || {};
  const [showNew,   setShowNew]   = useState(false);
  const [openReq,   setOpenReq]   = useState(null);
  const [detailView, setDetailView] = useState(null); // 'quote' | 'chat' | null
  const [reqTab, setReqTab] = useState('parts');
  const [expandedReqId, setExpandedReqId] = useState(null);
  const [, forceUpdate]           = useState();

  const requests = DB.get('requests') || [];
  const filtered = role.isCustomer
    ? requests.filter(r => r.customerId === user.id)
    : requests;

  const sorted = [...filtered].sort((a, b) =>
    (b.createdAt || '').localeCompare(a.createdAt || '')
  );

  const refreshKeepOpen = (reqId) => {
    forceUpdate({});
    const latest = (DB.get('requests') || []).find(r => r.id === reqId);
    if (latest) setOpenReq(latest);
  };

  const handleDeleteRequest = (reqId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this request permanently?')) return;
    const all = DB.get('requests') || [];
    DB.set('requests', all.filter(r => r.id !== reqId));
    forceUpdate({});
  };

  // ── Categorize requests ──
  const partsOrders = sorted.filter(r => r.type === 'parts_from_vehicle' || r.vehicleId);
  const searchRequests = sorted.filter(r => ['spec_search', 'photo_match', 'Vehicle', 'Engine'].includes(r.type) && !r.vehicleId);
  const messages = sorted.filter(r => !partsOrders.includes(r) && !searchRequests.includes(r));

  // Time ago helper
  const timeAgo = (d) => {
    if (!d) return '';
    const ms = Date.now() - new Date(d).getTime();
    if (ms < 3600000) return `${Math.floor(ms / 60000)}m ago`;
    if (ms < 86400000) return `${Math.floor(ms / 3600000)}h ago`;
    if (ms < 172800000) return 'yesterday';
    try { return new Date(d).toLocaleDateString([], { month: 'short', day: 'numeric' }); } catch { return d; }
  };

  const tabItems = reqTab === 'parts' ? partsOrders : reqTab === 'search' ? searchRequests : messages;
  
  const [statusFilter, setStatusFilter] = React.useState('');
  const [searchQ, setSearchQ] = React.useState('');

  const finalItems = tabItems.filter(r => {
    if (statusFilter && r.status !== statusFilter) return false;
    if (searchQ) {
      const q = searchQ.toLowerCase();
      const fields = [r.customerName, r.vehicleName, r.make, r.model, r.chassis, r.details, r.id].filter(Boolean).join(' ').toLowerCase();
      if (!fields.includes(q)) return false;
    }
    return true;
  });

  // Get live unit data for a request
  const getUnit = (request) => {
    if (!request.vehicleId) return null;
    const allU = [...(DB.get('vehicles') || []), ...(DB.get('engines') || []), ...(DB.get('spareParts') || [])];
    return allU.find(u => u.id === request.vehicleId) || null;
  };

  // Timeline stages helper
  const getTimelineStages = (status) => {
    const stages = [
      { id: 'submitted', label: 'Submitted', icon: '📝' },
      { id: 'reviewing', label: 'Review', icon: '🔍' },
      { id: 'pricing', label: 'Pricing', icon: '💰' },
      { id: 'quoted', label: 'Quoted', icon: '📨' },
      { id: 'completed', label: 'Done', icon: '🏁' },
    ];
    const map = { pending: ['submitted'], processing: ['submitted', 'reviewing'], reviewing: ['submitted', 'reviewing'], quoted: ['submitted', 'reviewing', 'pricing', 'quoted'], sourced: ['submitted', 'reviewing', 'pricing', 'quoted'], accepted: ['submitted', 'reviewing', 'pricing', 'quoted'], completed: ['submitted', 'reviewing', 'pricing', 'quoted', 'completed'] };
    const completed = map[status] || ['submitted'];
    return { stages, completed, current: completed[completed.length - 1] };
  };

  // ── Render Parts Order Card (expandable) ──
  const renderPartsCard = (request) => {
    const rp = request.requested_parts;
    const parts = Array.isArray(rp?.parts) ? rp.parts : Array.isArray(request.selectedPartsDetail) ? request.selectedPartsDetail : Array.isArray(request.selectedParts) ? request.selectedParts.map(p => typeof p === 'string' ? { part_name: p, quantity: 1 } : p) : [];
    const isExpanded = expandedReqId === request.id;
    const unit = getUnit(request);
    const unitName = request.vehicleName || (request.make ? `${request.make} ${request.model}` : 'Unit');
    const unitImg = unit?.images?.[0] || null;
    const tl = getTimelineStages(request.status);
    const partsSummary = parts.slice(0, 4).map(p => p.part_name || p).join(', ');

    return (
      <div key={request.id} className="card" style={{ marginBottom: 10 }}>
        <div className="card-body" style={{ padding: 0 }}>
          {/* ── Collapsed view ── */}
          <div style={{ padding: '14px 16px' }}>
            <div style={{ display: 'flex', gap: 12 }}>
              {/* Unit thumbnail */}
              <div style={{ width: 80, height: 60, borderRadius: 8, overflow: 'hidden', background: 'var(--bg3)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {unitImg ? <img src={unitImg} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 24, color: 'var(--text3)' }}>🚗</span>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Header row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                  <div>
                    {!role.isCustomer && <div style={{ fontWeight: 700, fontSize: 14 }}>{request.customerName}</div>}
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>#{request.id} · {timeAgo(request.createdAt)}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                    <span className={`status status-${STATUS_CLASS[request.status] || 'pending'}`} style={{ fontSize: 9 }}>
                      {REQUEST_STATUSES.find(s => s.id === request.status)?.name || request.status}
                    </span>
                    <button className="btn btn-secondary btn-sm" style={{ fontSize: 9, padding: '2px 6px' }} onClick={() => { setOpenReq(request); setDetailView('chat'); }}>Reply</button>
                    {role.canDelete && <button className="btn btn-secondary btn-sm" style={{ padding: '2px 5px', color: 'var(--red)', borderColor: 'var(--red)' }} onClick={e => handleDeleteRequest(request.id, e)}><DeleteIcon /></button>}
                  </div>
                </div>
                {/* Unit info */}
                <div style={{ fontWeight: 600, fontSize: 13 }}>🚗 {request.year ? `${request.year} ` : ''}{unitName}</div>
                <div style={{ fontSize: 10, color: 'var(--cyan)', fontFamily: 'monospace' }}>
                  {request.vehicleSku}{request.chassis ? ` · ${request.chassis}` : ''}
                </div>
                {unit && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>
                  {unit.score ? `Grade: ${unit.score}` : ''}{unit.mileage ? ` · ${unit.mileage?.toLocaleString()} km` : ''}{unit.transmission ? ` · ${unit.transmission}` : ''}
                </div>}
              </div>
            </div>

            {/* Parts summary */}
            <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 8 }}>
              <span style={{ fontWeight: 600, color: 'var(--text3)' }}>Parts ({parts.length}): </span>
              {partsSummary}{parts.length > 4 ? `, +${parts.length - 4} more` : ''}
            </div>

            {/* Mini timeline */}
            <div style={{ marginTop: 6 }}>
              <TimelineProgress stages={tl.stages} currentStage={tl.current} completedStages={tl.completed} compact />
            </div>

            {/* Expand toggle */}
            <button onClick={() => setExpandedReqId(isExpanded ? null : request.id)}
              style={{ background: 'none', border: 'none', color: 'var(--blue)', cursor: 'pointer', fontSize: 11, padding: '4px 0', fontWeight: 600, width: '100%', textAlign: 'center', marginTop: 4 }}>
              {isExpanded ? '▲ Collapse' : '▼ Expand Details'}
            </button>
          </div>

          {/* ── Expanded view ── */}
          {isExpanded && (
            <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--border)' }}>
              {/* Unit images */}
              {unit?.images?.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Unit Images</div>
                  <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
                    {unit.images.map((img, i) => (
                      <img key={i} src={img} alt="" onClick={() => window.open(img, '_blank')}
                        style={{ width: 100, height: 70, objectFit: 'cover', borderRadius: 6, cursor: 'pointer', flexShrink: 0 }} />
                    ))}
                  </div>
                </div>
              )}

              {/* Unit details grid */}
              {unit && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Unit Details</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 6, fontSize: 11 }}>
                    {[['Make', unit.make], ['Model', unit.model], ['Year', unit.year], ['Engine', unit.engineCode], ['Displacement', unit.displacement],
                      ['Color', unit.color], ['Steering', unit.steer], ['Drive', unit.driveSystem], ['Grade', unit.score || unit.grade],
                      ['Mileage', unit.mileage ? `${unit.mileage.toLocaleString()} km` : ''], ['Status', unit.status?.replace(/_/g, ' ')],
                    ].filter(([, v]) => v).map(([k, v]) => (
                      <div key={k}><span style={{ color: 'var(--text3)' }}>{k}: </span><span>{v}</span></div>
                    ))}
                  </div>
                </div>
              )}

              {/* Detailed parts table — prices are READ-ONLY */}
              {parts.length > 0 && (() => {
                // Check for FOB prices from Price Quotes
                const allQuotes = DB.get('priceQuotes') || [];
                const linkedQuote = allQuotes.find(q => q.sourceRequestId === request.id && Array.isArray(q.units));
                const getPartFob = (partName) => {
                  if (linkedQuote) {
                    const match = (linkedQuote.units || []).find(u => u._part_name === partName);
                    if (match?.fob_price || match?.pricing?.fob_price) return match.fob_price || match.pricing.fob_price;
                  }
                  // Also check partsProcessing
                  const pp = (request.partsProcessing || []).find(p => p.name === partName);
                  if (pp && Number(pp.price) > 0) return Number(pp.price);
                  return null;
                };
                const allPriced = parts.every(p => {
                  const pName = p.part_name || p;
                  return getPartFob(typeof pName === 'string' ? pName : pName.part_name) !== null;
                });
                const hasPricingRequest = !!request.pricingRequestedAt;

                return (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Requested Parts ({parts.length})</div>
                    <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 40px 80px 70px', padding: '6px 10px', background: 'var(--bg3)', fontSize: 9, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase' }}>
                        <span>Part</span><span>Qty</span><span>Price</span><span>Status</span>
                      </div>
                      {parts.map((p, i) => {
                        const pName = p.part_name || (typeof p === 'string' ? p : '');
                        const fob = getPartFob(pName);
                        return (
                          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 40px 80px 70px', padding: '6px 10px', fontSize: 11, borderTop: '1px solid var(--border)', alignItems: 'center' }}>
                            <span>{pName}</span>
                            <span style={{ color: 'var(--text3)' }}>{p.quantity || 1}</span>
                            <span style={{ fontWeight: 600, color: fob ? 'var(--green)' : 'var(--text3)' }}>
                              {fob ? `$${fob.toLocaleString()} ✅` : '—'}
                            </span>
                            <span style={{ fontSize: 9, color: fob ? 'var(--green)' : p.status === 'unavailable' ? 'var(--red)' : 'var(--yellow)' }}>
                              {fob ? 'priced' : p.status === 'unavailable' ? '❌ n/a' : '⏳ pending'}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {/* Action buttons for staff */}
                    {!role.isCustomer && (
                      <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        {request.vehicleId && <button className="btn btn-secondary btn-sm" style={{ fontSize: 10 }} onClick={() => {
                          setOpenReq(request); setDetailView('cutplan');
                        }}>✂️ Create Cut Plan</button>}
                        {!hasPricingRequest ? (
                          <button className="btn btn-primary btn-sm" style={{ fontSize: 10 }} onClick={() => {
                            // Quick request pricing from card
                            const pqList = DB.get('priceQuotes') || [];
                            const entries = parts.filter(p => (p.part_name || p) && p.status !== 'unavailable').map(p => ({
                              unit_id: `req_part_${request.id}_${p.part_name || p}`,
                              unit_type: 'spare_part',
                              unit_sku: request.vehicleSku || request.id,
                              unit_description: `${p.part_name || p} — from ${request.vehicleName || ''}`.trim(),
                              fob_price: null, pricing: null, status: 'pending',
                              accountant_notes: '', admin_approved: false,
                              _source_request_id: request.id, _part_name: p.part_name || p,
                            }));
                            const q = {
                              id: DB.genId('PQ'), customerId: request.customerId, customerName: request.customerName,
                              requestedBy: request.customerId, requestedByName: request.customerName,
                              status: 'pending', units: entries,
                              customer_notes: `Parts pricing for #${request.id}`, sourceRequestId: request.id,
                              createdAt: new Date().toISOString().split('T')[0], submittedAt: new Date().toISOString(),
                            };
                            DB.set('priceQuotes', [...pqList, q]);
                            const reqs = DB.get('requests') || [];
                            DB.set('requests', reqs.map(r => r.id === request.id ? { ...r, pricingRequestedAt: new Date().toISOString(), pricingQuoteId: q.id } : r));
                            Notifications.add('price_updated', 'Parts Pricing Needed',
                              `Parts pricing needed for ${request.customerName} — ${entries.length} parts from ${request.vehicleName || '#' + request.id}`, {});
                            forceUpdate({});
                          }}>💰 Request Pricing</button>
                        ) : (
                          <span style={{ fontSize: 10, color: 'var(--yellow)', fontWeight: 600 }}>Pricing Requested 🟡</span>
                        )}
                        <button className="btn btn-primary btn-sm" style={{ fontSize: 10 }}
                          disabled={!allPriced}
                          title={!allPriced ? 'All parts must be priced before sending quote. Send to accountant for pricing.' : ''}
                          onClick={() => { setOpenReq(request); setDetailView('quote'); }}>
                          💰 Send to Accountant
                        </button>
                        <button className="btn btn-secondary btn-sm" style={{ fontSize: 10 }} onClick={() => { setOpenReq(request); setDetailView('chat'); }}>💬 Reply</button>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Customer note */}
              {request.details && request.details !== `Parts request from ${unitName}` && (
                <div style={{ marginTop: 12, padding: '8px 10px', background: 'var(--bg3)', borderRadius: 6, fontSize: 11, color: 'var(--text2)', fontStyle: 'italic' }}>
                  "{request.details}"
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Render Search/Message Card ──
  const renderGenericCard = (request) => {
    const thread = getThread(request);
    const lastMsg = thread[thread.length - 1];
    return (
      <div key={request.id} className="card" style={{ marginBottom: 8 }}>
        <div className="card-body" style={{ padding: '14px 16px' }}>
          <div style={{ display: 'flex', gap: 12 }}>
            {/* Icon/photo */}
            <div style={{ width: 56, height: 42, borderRadius: 6, background: 'var(--bg3)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
              {request.type === 'photo_match' ? '📷' : request.type === 'support' || request.type === 'general' ? '💬' : '🔍'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                <div>
                  {!role.isCustomer && <div style={{ fontWeight: 700, fontSize: 14 }}>{request.customerName}</div>}
                  <div style={{ fontSize: 10, color: 'var(--text3)' }}>#{request.id} · {timeAgo(request.createdAt)}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                  <span className={`status status-${STATUS_CLASS[request.status] || 'pending'}`} style={{ fontSize: 9 }}>
                    {REQUEST_STATUSES.find(s => s.id === request.status)?.name || request.status}
                  </span>
                  <button className="btn btn-secondary btn-sm" style={{ fontSize: 9, padding: '2px 6px' }} onClick={() => { setOpenReq(request); setDetailView('chat'); }}>Reply</button>
                  {role.canDelete && <button className="btn btn-secondary btn-sm" style={{ padding: '2px 5px', color: 'var(--red)', borderColor: 'var(--red)' }} onClick={e => handleDeleteRequest(request.id, e)}><DeleteIcon /></button>}
                </div>
              </div>
              {request.make && <div style={{ fontSize: 12, marginBottom: 2 }}>{request.make} {request.model} {request.year}</div>}
              {request.budget && request.budget !== '1' && request.budget !== '2' && <div style={{ fontSize: 10, color: 'var(--blue)', marginBottom: 2 }}>Budget: {request.budget}</div>}
              <p style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.4, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                "{request.details}"
              </p>
            </div>
          </div>
          {lastMsg && (
            <div style={{ marginTop: 8, padding: '6px 10px', background: 'rgba(88,166,255,0.06)', borderLeft: '3px solid var(--blue)', borderRadius: '0 6px 6px 0', fontSize: 11, color: 'var(--text2)' }}>
              <strong>{lastMsg.author}:</strong> {(lastMsg.message || '').slice(0, 80)}{lastMsg.message?.length > 80 ? '…' : ''}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <h2>{t('requests')}</h2>
        {role.isCustomer && (
          <button className="btn btn-primary" onClick={() => setShowNew(true)}>
            <PlusIcon /> New Request
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 14 }}>
        <button className={`tab ${reqTab === 'parts' ? 'active' : ''}`} onClick={() => setReqTab('parts')}>
          🔧 Parts Orders <span className="cnt">{partsOrders.length}</span>
        </button>
        <button className={`tab ${reqTab === 'search' ? 'active' : ''}`} onClick={() => setReqTab('search')}>
          🔍 Search <span className="cnt">{searchRequests.length}</span>
        </button>
        <button className={`tab ${reqTab === 'messages' ? 'active' : ''}`} onClick={() => setReqTab('messages')}>
          💬 Messages <span className="cnt">{messages.length}</span>
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <select className="inv-filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="processing">In Progress</option>
          <option value="quoted">Quoted</option>
          <option value="sourced">Sourced</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <div className="inv-search-wrap">
          <input className="inv-search" placeholder="Search..." value={searchQ} onChange={e => setSearchQ(e.target.value)} style={{ width: 160 }} />
        </div>
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>{finalItems.length} requests</span>
      </div>

      {/* List */}
      {finalItems.length === 0 ? (
        <div className="empty">
          <RequestIcon />
          <div className="empty-title">No {reqTab === 'parts' ? 'parts orders' : reqTab === 'search' ? 'search requests' : 'messages'}</div>
          {role.isCustomer && (
            <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={() => setShowNew(true)}>
              <PlusIcon /> New Request
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {finalItems.map(r => reqTab === 'parts' ? renderPartsCard(r) : renderGenericCard(r))}
        </div>
      )}

      {showNew && <NewRequestModal user={user} onSave={() => { setShowNew(false); forceUpdate({}); }} onClose={() => setShowNew(false)} />}

      {openReq && (
        <RequestThreadModal
          request={openReq}
          user={user}
          role={role}
          initialView={detailView}
          onSave={() => refreshKeepOpen(openReq.id)}
          onClose={() => { setOpenReq(null); setDetailView(null); forceUpdate({}); }}
        />
      )}
    </div>
  );
}

export default Requests;
